import React, { useState } from 'react';
import { removeCrmLeadContact, updateCrmLeadContact } from '../config/api';

const CrmEditContactDialog = ({ leadId, contact, onClose, onSaved }) => {
  const phoneIsMasked = /x/i.test(contact.phone || '');
  const [form, setForm] = useState({
    name: contact.name || '',
    phone: phoneIsMasked ? '' : (contact.phone || ''),
    designation: contact.designation || '',
    isPrimary: !!contact.isPrimary,
    doNotCall: !!contact.doNotCall,
    noWhatsapp: !!contact.noWhatsapp,
  });
  const [saving, setSaving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (error) setError('');
  };

  const save = async (event) => {
    event.preventDefault();
    const digits = form.phone.replace(/\D/g, '').slice(-10);
    if ((!phoneIsMasked || form.phone.trim()) && digits.length !== 10) {
      setError('Enter a valid 10-digit phone number.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        designation: form.designation.trim(),
        isPrimary: form.isPrimary,
        doNotCall: form.doNotCall,
        noWhatsapp: form.noWhatsapp,
      };
      if (!phoneIsMasked || form.phone.trim()) payload.phone = digits;
      await updateCrmLeadContact(leadId, contact.id, payload);
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not update the contact.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError('');
    try {
      await removeCrmLeadContact(leadId, contact.id);
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not remove the contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <form onSubmit={save}>
          <div className="modal-header">
            <div>
              <h3>Edit contact</h3>
              <div className="crm-modal-subtitle">Update this contact or remove it from the Lead.</div>
            </div>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="crm-edit-contact-name">Name</label>
              <input
                id="crm-edit-contact-name"
                type="text"
                className="form-control"
                value={form.name}
                maxLength={255}
                onChange={(event) => update('name', event.target.value)}
                disabled={saving}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="crm-edit-contact-phone">Phone (10 digits)</label>
              {phoneIsMasked && (
                <div className="crm-current-masked-phone">Current phone: {contact.phone}</div>
              )}
              <input
                id="crm-edit-contact-phone"
                type="tel"
                className="form-control"
                value={form.phone}
                onChange={(event) => update('phone', event.target.value)}
                placeholder={phoneIsMasked ? 'Leave blank to keep the current phone' : '9876543210'}
                inputMode="numeric"
                disabled={saving}
              />
              {phoneIsMasked && (
                <div className="crm-form-help">Enter a complete new number only when replacing it.</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="crm-edit-contact-designation">Designation</label>
              <input
                id="crm-edit-contact-designation"
                type="text"
                className="form-control"
                value={form.designation}
                maxLength={120}
                onChange={(event) => update('designation', event.target.value)}
                disabled={saving}
              />
            </div>
            <div className="crm-contact-options">
              <label>
                <input
                  type="checkbox"
                  checked={form.isPrimary}
                  onChange={(event) => update('isPrimary', event.target.checked)}
                  disabled={saving}
                />
                Primary contact
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.doNotCall}
                  onChange={(event) => update('doNotCall', event.target.checked)}
                  disabled={saving}
                />
                Do not call
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.noWhatsapp}
                  onChange={(event) => update('noWhatsapp', event.target.checked)}
                  disabled={saving}
                />
                No WhatsApp
              </label>
            </div>
            {confirmingRemove && (
              <div className="crm-delete-confirm">
                <strong>Remove this contact?</strong>
                <span>The contact will no longer appear on this Lead.</span>
                <div>
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirmingRemove(false)} disabled={saving}>
                    Keep contact
                  </button>
                  <button type="button" className="btn btn-danger" onClick={remove} disabled={saving}>
                    {saving ? 'Removing…' : 'Yes, remove'}
                  </button>
                </div>
              </div>
            )}
            {error && <div className="crm-form-error">{error}</div>}
          </div>
          <div className="modal-footer crm-modal-footer-split">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setConfirmingRemove(true)}
              disabled={saving || confirmingRemove}
            >
              Remove contact
            </button>
            <div>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving || confirmingRemove}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmEditContactDialog;
